import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '../lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Navbar } from '../components/navbar';
import { Sidebar } from '../components/sidebar';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];

const ResultsPage: React.FC = () => {
  const { data: results, isLoading, isError, error } = useQuery({
    queryKey: ['ghg-results'],
    queryFn: () => apiRequest('GET', '/ghg/results').then(res => res.json()),
  });

  const scopeData = React.useMemo(() => {
    if (!results) return [];
    const scopeTotals = results.reduce((acc: any, curr: any) => {
      const scope = curr.scope.toUpperCase();
      if (!acc[scope]) {
        acc[scope] = 0;
      }
      acc[scope] += curr.co2_equivalent;
      return acc;
    }, {});

    return Object.keys(scopeTotals).map(scope => ({
      name: scope,
      value: scopeTotals[scope],
    }));
  }, [results]);

  const totalEmissions = scopeData.reduce((acc, curr) => acc + curr.value, 0);

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-200 p-6">
          <Card>
            <CardHeader>
              <CardTitle>GHG Estimation Results</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading && <p>Loading results...</p>}
              {isError && <p className="text-red-500">Error fetching results: {error.message}</p>}
              {results && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-sm font-semibold mb-3">Emissions Breakdown by Scope</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={scopeData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {scopeData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-col justify-center">
                      <h3 className="text-sm font-semibold">Total Estimated Emissions</h3>
                      <p className="text-4xl font-bold">{totalEmissions.toFixed(2)} kg CO₂e</p>
                    </div>
                  </div>

                  <h3 className="text-sm font-semibold mb-3">All Estimations</h3>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scope</TableHead>
                        <TableHead>Activity</TableHead>
                        <TableHead>CO₂e (kg)</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {results.map((result: any) => (
                        <TableRow key={result.id}>
                          <TableCell>{result.scope.toUpperCase()}</TableCell>
                          <TableCell>{result.estimation_method}</TableCell>
                          <TableCell>{result.co2_equivalent.toFixed(2)}</TableCell>
                          <TableCell>{new Date(result.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
};

export default ResultsPage;
